<template>
  <div :class="jel_getClassObject(content)">
    <div>
      <div
        v-if="isSpinnerVisible"
        id="spinner"
        class="d-flex flex-column justify-content-center align-items-center text-center"
      >
        <b-spinner :label="$t('chargement')"></b-spinner>
      </div>
      <div class="row">
        <div class="col text-left">
          <!-- Liste Avis -->
          <template v-if="!isErreurServeur">
            <div v-if="messages.length" class="avis-view">
              <div
                class="avis-list"
                :class="{ 'avis-list--hidden': isListHidden && !isDesktopLayout }"
                :inert="isListInactive"
              >
                <button
                  v-for="(messageAvis, index) in visibleMessages"
                  :key="
                    messageAvis.id ||
                    `${messageAvis.messageCode}-${messageAvis.createDate}-${index}`
                  "
                  :ref="`avis-${messageAvis.id}`"
                  type="button"
                  class="avis-list__item"
                  :class="{
                    'avis-list__item--unread': !messageAvis.readDate,
                    'avis-list__item--read': Boolean(messageAvis.readDate),
                    'avis-list__item--selected': isSelected(messageAvis),
                  }"
                  :aria-expanded="isSelected(messageAvis) ? 'true' : 'false'"
                  :tabindex="isListInactive ? -1 : 0"
                  :disabled="isMessageIndisponible(messageAvis)"
                  @click="openMessage(messageAvis)"
                >
                  <span class="avis-list__text">
                    <span class="avis-list__title">{{ getMessageTitle(messageAvis) }}</span>
                    <time class="avis-list__date" :datetime="messageAvis.createDate">
                      {{ formatMessageDate(messageAvis.createDate) }}
                    </time>
                  </span>
                  <span
                    v-if="!isMessageIndisponible(messageAvis)"
                    class="avis-list__chevron"
                    aria-hidden="true"
                  ></span>
                </button>

                <button
                  v-if="showLoadMore"
                  type="button"
                  class="avis-load-more"
                  :disabled="isLoadingMore"
                  :tabindex="isListInactive ? -1 : 0"
                  @click="loadMoreMessages"
                >
                  <b-spinner v-if="isLoadingMore" small :label="$t('chargement')"></b-spinner>
                  <span v-else>{{ loadMoreLabel }}</span>
                </button>
                <button
                  v-else-if="showLoadLess"
                  type="button"
                  class="avis-load-more"
                  :tabindex="isListInactive ? -1 : 0"
                  @click="showInitialMessages"
                >
                  {{ loadLessLabel }}
                </button>
              </div>
              <transition
                name="avis-detail-panel"
                @after-enter="setBackButtonFocus"
                @after-leave="restoreListFocus"
              >
                <section
                  v-if="selectedMessage || isDesktopLayout"
                  key="detail"
                  class="avis-detail"
                  aria-live="polite"
                >
                  <button
                    v-if="selectedMessage"
                    ref="backButton"
                    type="button"
                    class="avis-detail__back"
                    :aria-label="$t('avisBienJouer.retourListeAria')"
                    @click="closeMessage"
                  >
                    <span class="avis-detail__back-chevron" aria-hidden="true"></span>
                    <span>{{ $t('avisBienJouer.retourListe') }}</span>
                  </button>

                  <div v-if="selectedMessage" class="avis-detail__box">
                    <time class="avis-detail__date" :datetime="selectedMessage.createDate">
                      {{ formatMessageDate(selectedMessage.createDate) }}
                    </time>
                    <h3 class="avis-detail__title">{{ getMessageTitle(selectedMessage) }}</h3>
                    <div class="Paragraphe" v-html="getMessageDetail(selectedMessage)" />
                  </div>
                  <div v-else class="avis-detail__box avis-detail__box--empty">
                    <svg-icon icon-name="ic-enveloppe-bien-jouer" class="avis-detail__empty-icon" />
                    <p class="avis-detail__empty-text">
                      {{ $t('avisBienJouer.selectionnerMessage') }}
                    </p>
                  </div>
                </section>
              </transition>
            </div>

            <p v-else-if="!isSpinnerVisible" class="avis-empty">
              {{ $t('avisBienJouer.aucunAvis') }}
            </p>
          </template>
          <div v-else>
            <MessageMel
              v-show="contentMessageMel.message"
              :content="contentMessageMel.message"
              class="mt-n1 mt-md-0 mb-1 mx-0 mx-md-auto pl-0"
            ></MessageMel>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import session from '@/services/api/compte/session';
import activite from '@/services/api/compte/activiteJeux';
import MessageHelpers from '@/helpers/MessageHelpers';
import { inMgnl } from '@/helpers/AppHelpers';
import MessageMel from '@/components/base/MessageMel';
import { PageMapping } from '@/helpers/PageMappingHelpers';
import { gtmCustomEventByVendor } from '@/helpers/ConsentHelpers';
import SvgIcon from '@/helpers/SvgIcon.vue';

const DEFAULT_MESSAGE_LIMIT = 10;
const DESKTOP_LAYOUT_BREAKPOINT = '(min-width: 550px)';

export default {
  name: 'CompteAvisBienJouer',
  components: {
    MessageMel,
    SvgIcon,
  },
  props: {
    content: { type: Object, default: () => ({}) },
    metadata: { type: Object, default: null },
  },
  i18n: {
    messages: {
      fr: {        
        avisBienJouer: {
          aucunAvis: 'Aucun message',
          retourListe: 'Messages',
          retourListeAria: 'Retour à la liste des messages',
          voirMoins: 'Voir moins',
          voirPlus: 'Voir plus',
          selectionnerMessage: 'Sélectionnez un message.',
          messageNonDisponible: 'Message non disponible',
        },
      },
      en: {
        avisBienJouer: {
          aucunAvis: 'No messages',
          retourListe: 'Messages',
          retourListeAria: 'Back to the message list',
          voirMoins: 'See less',
          voirPlus: 'See more',
          selectionnerMessage: 'Select a message.',
          messageNonDisponible: 'Message unavailable',
        },
      },
    },
  },
  data() {
    return {
      isSpinnerVisible: true,
      message: null,
      contentMessageMel: {
        message: {},
        alert: {},
      },
      isErreurServeur: false,
      gameActivity: {},
      selectedPeriod: null,
      uuid: null,
      messagesBienJouer: {},
      selectedMessage: null,
      visibleCount: 0,
      initialVisibleCount: 0,
      hasMoreMessages: false,
      isLoadingMore: false,
      lastSelectedMessageId: null,
      isDesktopLayout: false,
      isListHidden: false,
    };
  },
  computed: {
    isFeatureNecctonEnabled() {
      return this.content.featureNeccton === 'true';
    },
    messages() {
      return this.$store.state.activiteJeux.messages;
    },
    isListInactive() {
      return !this.isDesktopLayout && Boolean(this.selectedMessage);
    },
    messageLimit() {
      const configuredLimit = Number(this.content.messageLimit);
      return Number.isInteger(configuredLimit) && configuredLimit > 0
        ? configuredLimit
        : DEFAULT_MESSAGE_LIMIT;
    },
    loadLessLabel() {
      return this.content.libBtnAfficherMoins || this.$t('avisBienJouer.voirMoins');
    },
    loadMoreLabel() {
      return this.content.libBtnAfficherPlus || this.$t('avisBienJouer.voirPlus');;
    },
    remainingMessagesCount() {
      return Math.max(this.messages.length - this.visibleCount, 0);
    },
    showLoadMore() {
      return this.remainingMessagesCount > 0 || this.hasMoreMessages;
    },
    showLoadLess() {
      return this.visibleCount > this.initialVisibleCount;
    },
    visibleMessages() {
      return this.messages.slice(0, this.visibleCount);
    },
    contentLienAideTempsJeux() {
      if (!this.content.libAideTempsJeux) {
        return null;
      }
      return {
        texte: this.content.libAideTempsJeux,
        type: 'aide text-decoration-none',
        idQuestion: this.content.idQuestionTempsJeux,
        lien: {
          field: 'aide',
          idAide: this.content.idQuestionTempsJeux,
        },
      };
    },
  },

  created() {
    this.desktopLayoutMediaQuery = null;

    if (!this.isFeatureNecctonEnabled) {
      this.$router.push({ path: PageMapping.ERREUR_GENERIQUE.siteUrl });
    }
  },

  async mounted() {
    if (!this.isFeatureNecctonEnabled) {
      return;
    }

    this.initializeDesktopLayout();

    // Afficher le spinner au chargement de la page sauf dans Magnolia
    if (inMgnl) {
      this.isSpinnerVisible = false;
    }
    this.uuid = sessionStorage.getItem('lq_client_uuid');
    this.$store.commit('activiteJeux/RESET_MESSAGES');

    const requiresUuid = process.env.VUE_APP_IS_MOCK !== 'true' && !inMgnl;
    if (requiresUuid && !this.uuid) {
      this.isSpinnerVisible = false;
      return;
    }

    await this.fetchMessages();
    this.updateMessageCount();
  },
  beforeDestroy() {
    if (!this.desktopLayoutMediaQuery) {
      return;
    }

    if (this.desktopLayoutMediaQuery.removeEventListener) {
      this.desktopLayoutMediaQuery.removeEventListener('change', this.handleDesktopLayoutChange);
    } else {
      this.desktopLayoutMediaQuery.removeListener(this.handleDesktopLayoutChange);
    }
  },
  methods: {
    initializeDesktopLayout() {
      this.desktopLayoutMediaQuery = window.matchMedia(DESKTOP_LAYOUT_BREAKPOINT);
      this.handleDesktopLayoutChange(this.desktopLayoutMediaQuery);

      if (this.desktopLayoutMediaQuery.addEventListener) {
        this.desktopLayoutMediaQuery.addEventListener('change', this.handleDesktopLayoutChange);
      } else {
        this.desktopLayoutMediaQuery.addListener(this.handleDesktopLayoutChange);
      }
    },

    handleDesktopLayoutChange(mediaQuery) {
      this.isDesktopLayout = mediaQuery.matches;
      if (this.isDesktopLayout) {
        this.isListHidden = false;
      }
    },

    async saveMessages(fetchedMessages) {
      this.$store.commit('activiteJeux/SET_MESSAGES', fetchedMessages);
      await this.loadMessageContents(fetchedMessages);
    },

    async appendMessages(fetchedMessages) {
      this.$store.commit('activiteJeux/APPEND_MESSAGES', fetchedMessages);
      await this.loadMessageContents(fetchedMessages);
    },

    closeMessage() {
      if (this.isDesktopLayout) {
        return;
      }

      this.isListHidden = false;
      this.lastSelectedMessageId = this.selectedMessage && this.selectedMessage.id;
      this.selectedMessage = null;
    },

    restoreListFocus() {
      const messageButton = this.$refs[`avis-${this.lastSelectedMessageId}`];
      const button = Array.isArray(messageButton) ? messageButton[0] : messageButton;

      if (button) {
        button.focus();
      }

      this.lastSelectedMessageId = null;
    },

    async fetchMessages() {
      try {
        const messagesResponse = await activite.getMessages(this.uuid, this.messageLimit, 0);
        const fetchedMessages = Array.isArray(messagesResponse)
          ? messagesResponse.slice(0, this.messageLimit)
          : [];

        await this.saveMessages(fetchedMessages);
        this.initialVisibleCount = fetchedMessages.length;
        this.visibleCount = fetchedMessages.length;
        this.hasMoreMessages = fetchedMessages.length === this.messageLimit;
      } catch (error) {
        console.error('Erreur lors du chargement des avis Bien jouer :', error);
        this.isErreurServeur = true;
        this.recupererMessage('MSG01', 'message');
      } finally {
        this.isSpinnerVisible = false;
      }
    },

    formatMessageDate(dateValue) {
      if (!dateValue) {
        return '';
      }

      const datePart = String(dateValue).split(/[ T]/)[0];
      return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart.replace(/-/g, '/') : datePart;
    },

    getMessageDetail(messageAvis) {
      const messageContent = this.messagesBienJouer[messageAvis.messageCode] || {};
      return (
        messageContent.msgDetail ||
        messageAvis.msgDetail ||
        messageAvis.message ||
        messageAvis.detail ||
        ''
      );
    },

    getMessageTitle(messageAvis) {
      const messageContent = this.messagesBienJouer[messageAvis.messageCode] || {};
      if (messageContent.msgInexistant) {
        return this.$t('avisBienJouer.messageNonDisponible');
      }
      return (
        messageContent.msgTitre ||
        messageAvis.msgTitre ||
        messageAvis.title ||
        messageAvis.messageCode ||
        ''
      );
    },

    isMessageIndisponible(messageAvis) {
      const messageContent = this.messagesBienJouer[messageAvis.messageCode];
      return Boolean(messageContent && messageContent.msgInexistant);
    },

    isSelected(messageAvis) {
      return Boolean(this.selectedMessage && this.selectedMessage.id === messageAvis.id);
    },

    async loadMessageContents(messages) {
      const messageCodes = [
        ...new Set(
          messages
            .map((messageAvis) => messageAvis.messageCode)
            .filter((messageCode) => messageCode && !this.messagesBienJouer[messageCode])
        ),
      ];

      await Promise.all(
        messageCodes.map(async (messageCode) => {
          const messageContent = await MessageHelpers.obtenirMessage(messageCode, this.$root);
          this.$set(this.messagesBienJouer, messageCode, messageContent);
        })
      );
    },

    async markMessageAsRead(messageAvis) {
      try {
        await activite.setReadMessage(this.uuid, messageAvis.id);
        this.$store.commit('activiteJeux/SET_MESSAGE_AS_READ', {
          messageId: messageAvis.id,
          readDate: new Date().toISOString(),
          readState: true
        });
      } catch (error) {
        console.error('Erreur lors de la mise à jour du message comme lu :', error);
      }
    },

    updateMessageCount() {
      document.dispatchEvent(new CustomEvent('updateNecctonUnreadMessages', {
        detail: {
          successHandler: function(event) {
            console.log('Mise à jour du nb de messages non lus ', event);
          },
          errorHandler: function() {
            console.log('Erreur lors de la mise à jour du nb de messages non lus');
          }
        }
      }));
    },

    async openMessage(messageAvis) {
      this.selectedMessage = messageAvis;
      this.sendAnalytics(messageAvis);
      if (messageAvis.readState) {
        return;
      }
      await this.markMessageAsRead(messageAvis);
      this.updateMessageCount();
    },

    sendAnalytics(messageAvis) {
      // Ajout datalayer pour Google Analytics
      gtmCustomEventByVendor(process.env.VUE_APP_IAB_ID_ANALYTICS, {
        event: 'ga-neccton-messages',
        label: messageAvis?.messageCode || 'unknown',
      });
    },

    setBackButtonFocus() {
      if (this.isDesktopLayout) {
        return;
      }

      this.isListHidden = true;
      if (this.$refs.backButton) {
        this.$refs.backButton.focus();
      }
    },

    async loadMoreMessages() {
      if (this.isLoadingMore) {
        return;
      }

      if (this.remainingMessagesCount > 0) {
        this.visibleCount = Math.min(this.visibleCount + this.messageLimit, this.messages.length);
        return;
      }

      if (!this.hasMoreMessages) {
        return;
      }

      this.isLoadingMore = true;

      try {
        const messagesResponse = await activite.getMessages(
          this.uuid,
          this.messageLimit,
          this.messages.length
        );
        const fetchedMessages = Array.isArray(messagesResponse)
          ? messagesResponse.slice(0, this.messageLimit)
          : [];
        await this.appendMessages(fetchedMessages);

        this.visibleCount = this.messages.length;
        this.hasMoreMessages = fetchedMessages.length === this.messageLimit;
      } catch (error) {
        console.error('Erreur lors du chargement des messages supplémentaires :', error);
        this.isErreurServeur = true;
        this.recupererMessage('MSG01', 'message');
      } finally {
        this.isLoadingMore = false;
      }
    },

    showInitialMessages() {
      this.visibleCount = this.initialVisibleCount;
    },

    verifierSessionOuverte() {
      // eslint-disable-next-line no-restricted-globals
      event.preventDefault();
      if (this.isSessionOuverte()) {
        return true;
      }
      return false;
    },

    isSessionOuverte() {
      return session.sessionEstOuverte();
    },

    viderMessageMel() {
      this.contentMessageMel.message = '';
    },

    recupererMessage(codeMessage, contentMessage) {
      MessageHelpers.obtenirMessage(codeMessage, this.$root)
        .then((msgResp) => {
          this.contentMessageMel[contentMessage] = {
            titre: msgResp.msgTitre,
            message: msgResp.msgDetail,
            msgType: msgResp.msgType,
          };
        })
        .catch((error) => {
          console.error('Erreur de récupération du message!', error);
        });
    },
  },
};
</script>
<style lang="scss">
.container.avis-bien-jouer-container {
  max-width: 100%;
  padding: 16px;
}

@media (min-width: 550px) {
  .container.avis-bien-jouer-container {
    max-width: 740px;
    padding: 24px;
  }
}
</style>
<style lang="scss" scoped>
.avis-view,
.avis-empty {
  max-width: 600px;
  margin-right: auto;
  margin-left: auto;
}

.avis-view {
  position: relative;
  display: grid;
  overflow: hidden;
}

.avis-list,
.avis-detail {
  grid-area: 1 / 1;
  min-width: 0;
}

.avis-detail {
  z-index: 2;
  align-self: stretch;
  background: #fff;
  display: flex;
  flex-direction: column;
}

.avis-detail__back {
  display: inline-flex;
  min-height: 40px;
  padding: 8px;
  font-size: 14px;
  font-weight: bold;
  background: transparent;
  border: 0;
  align-items: center;
  gap: 8px;
  color: $couleur-bleu-fonce-lq;

  &:focus,
  &:focus-visible {
    outline: none;
  }
}

.avis-detail__back-chevron {
  width: 8px;
  height: 8px;
  border-bottom: 1px solid currentColor;
  border-left: 1px solid currentColor;
  transform: rotate(45deg);
}

.avis-detail__box {
  padding: 15px;
  background: #fff;
  border: 4px solid $couleur-bg-form-bien-jouer;
  border-radius: 8px;
  flex: 1;
  font-size: 14px;
  overflow: auto;
}

.avis-detail__date {
  display: block;
  font-size: 14px;
  color: $couleur-bleu-fonce-lq;
}

.avis-detail__title {
  padding-top: 0px;
  margin: 0 0 20px;
  font-size: 14px;
  font-weight: bold;
  color: $couleur-bleu-fonce-lq;
}

.avis-detail__box--empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  gap: 5px;
}

.avis-detail__empty-icon {
  width: 32px;
  height: 32px;
  color: $couleur-bleu-fonce-lq;
}

.avis-detail__empty-text {
  margin: 0;
  font-size: 14px;
  color: $couleur-bleu-fonce-lq;
}

.avis-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.avis-list--hidden {
  visibility: hidden;
}

.avis-list__item {
  position: relative;
  display: flex;
  width: 100%;
  min-height: 64px;
  padding: 8px 42px 8px 13px;
  overflow: hidden;
  text-align: left;
  color: $couleur-bleu-fonce-lq;
  background: $couleur-bg-form-bien-jouer;
  border: 0;
  align-items: center;
  border-radius: 8px;
/*
  &:hover,
  &:focus-visible,*/
  &--selected,
  &--selected:focus:not(:focus-visible) {
    z-index: 1;
    border: 2px solid #0078dc;
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.avis-list__item--unread .avis-list__title,
.avis-list__item--unread .avis-list__date,
.avis-list__item--selected .avis-list__title {
  font-weight: bold;
}

.avis-list__item--read .avis-list__title,
.avis-list__item--read .avis-list__date {
  font-weight: normal;
}

.avis-list__text {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.avis-list__title {
  overflow: hidden;
  font-size: 14px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.avis-list__date {
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.2;
}

.avis-list__chevron {
  position: absolute;
  top: 50%;
  right: 18px;
  width: 8px;
  height: 8px;
  border-top: 1.5px solid currentColor;
  border-right: 1.5px solid currentColor;
  transform: translateY(-50%) rotate(45deg);
}

.avis-load-more {
  min-height: 44px;
  padding: 12px 18px;
  align-self: center;
  font-size: 15px;
  color: #020a30;
  background: transparent;
  border: 0;

  &:hover,
  &:focus {
    text-decoration: underline;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
}

.avis-empty {
  padding: 12px 0;
  text-align: center;
}

/* Animation pour avis-detail */
.avis-detail-panel-enter-active,
.avis-detail-panel-leave-active {
  transition: transform 0.3s ease-in-out;
  transition: -webkit-transform 0.3s ease-in-out;
}

.avis-detail-panel-enter,
.avis-detail-panel-leave-to {
  transform: translate3d(100%, 0, 0);
}

@media (prefers-reduced-motion: reduce) {
  .avis-detail-panel-enter-active,
  .avis-detail-panel-leave-active {
    transition: none;
  }
}

@media (min-width: 550px) {
  .container.avis-bien-jouer-container {
    max-width: 640px;
    padding: 20px 0;
  }

  .avis-view {
    max-width: none;
    overflow: visible;
    grid-template-columns: minmax(0, 250px) minmax(0, 2fr);
    column-gap: 8px;
  }

  .avis-list,
  .avis-detail {
    grid-area: auto;
  }

  .avis-list {
    max-height: 400px;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .avis-detail {
    z-index: auto;
    height: 400px;
  }

  .avis-detail__back {
    display: none;
  }

  .avis-detail-panel-enter-active,
  .avis-detail-panel-leave-active {
    transition: none;
  }

  .avis-detail-panel-enter,
  .avis-detail-panel-leave-to {
    transform: none;
  }
}

</style>
